import React, { useState, useEffect } from 'react'
import userApi from '_api/user'
import { getUserInfo } from '_utils/localData'
import { useModal } from '_hooks/useModal'
import AddAddressModal from './components/AddAddressModal'
import EditAddressModal from './components/EditAddressModal'

const formatAddressName = (name) => {
  if (!name) return 'Address'
  const trimmed = name.trim()
  if (trimmed.toUpperCase() === 'HOME') return 'Home'
  if (trimmed.toUpperCase() === 'OFFICE') return 'Office'
  return trimmed
}

const AddressesTab = () => {
  const [addresses, setAddresses] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const addModal = useModal(false)
  const editModal = useModal(false)
  const [selectedAddress, setSelectedAddress] = useState(null)
  const [userInfo, setUserInfo] = useState({})

  const fetchAddresses = async () => {
    try {
      setIsLoading(true)
      if (userApi.listUserAddress) {
        const response = await userApi.listUserAddress()
        const addressData = response?.addresses || response?.msgResp || []
        if (Array.isArray(addressData) && addressData.length > 0) {
          const sorted = [...addressData].sort((a, b) => {
            if (a.isDefault && !b.isDefault) return -1
            if (!a.isDefault && b.isDefault) return 1
            return 0
          })
          setAddresses(sorted)
        } else {
          setAddresses([])
        }
      } else {
        setAddresses([])
      }
    } catch (error) {
      console.warn('Fetch addresses error:', error)
      setAddresses([])
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    setUserInfo(getUserInfo() || {})
    fetchAddresses()
  }, [])

  const handleAddAddress = () => {
    addModal.openModal()
  }

  const handleEditAddress = (addr, e) => {
    if (e) e.stopPropagation()
    setSelectedAddress(addr)
    editModal.openModal()
  }

  const renderIcon = (addr) => {
    const rawName = (addr.name || addr.type || '').toLowerCase()
    const isHome = rawName.includes('home') || (!rawName.includes('office') && addr.isDefault)
    const isOffice = rawName.includes('office') || rawName.includes('work')

    if (isHome) {
      return (
        <svg
          width='15'
          height='15'
          viewBox='0 0 24 24'
          fill='none'
          stroke={addr.isDefault ? '#009A97' : '#888780'}
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' />
        </svg>
      )
    }

    if (isOffice) {
      return (
        <svg
          width='15'
          height='15'
          viewBox='0 0 24 24'
          fill='none'
          stroke={addr.isDefault ? '#009A97' : '#888780'}
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <rect x='2' y='7' width='20' height='14' rx='2' />
          <path d='M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16' />
        </svg>
      )
    }

    return (
      <svg
        width='15'
        height='15'
        viewBox='0 0 24 24'
        fill='none'
        stroke={addr.isDefault ? '#009A97' : '#888780'}
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
        <circle cx='12' cy='10' r='3' />
      </svg>
    )
  }

  return (
    <div className='tab-addresses-view'>
      <div className='section-card'>
        <div className='section-hd'>
          <div className='section-title'>
            <svg
              width='15'
              height='15'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
              <circle cx='12' cy='10' r='3' />
            </svg>
            Saved Addresses
          </div>
          <button type='button' className='section-action' onClick={handleAddAddress}>
            + Add
          </button>
        </div>

        <div className='address-list'>
          {isLoading ? (
            <div className='address-loading-state'>
              <div className='spinner-border spinner-border-sm text-secondary' role='status' />
            </div>
          ) : addresses.length === 0 ? (
            <div className='address-empty-state'>
              <div className='empty-icon'>
                <svg
                  width='22'
                  height='22'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='1.8'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                >
                  <path d='M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z' />
                  <circle cx='12' cy='10' r='3' />
                </svg>
              </div>
              <div className='empty-title'>No saved addresses</div>
              <div className='empty-sub'>You have not added any delivery address yet.</div>
              <button
                type='button'
                className='empty-action-btn'
                onClick={handleAddAddress}
              >
                + Add Address
              </button>
            </div>
          ) : (
            addresses.map((addr, idx) => (
              <div key={addr.id || idx} className='address-row'>
                <div className='address-row-left'>
                  <div
                    className='address-row-icon'
                    style={{ background: addr.isDefault ? '#E0F5F5' : '#f0f0f0' }}
                  >
                    {renderIcon(addr)}
                  </div>
                  <div className='address-row-info'>
                    <div className='address-row-label'>
                      <span>{formatAddressName(addr.name || addr.type)}</span>
                      {addr.isDefault && (
                        <span className='address-default-badge'>Default</span>
                      )}
                    </div>
                    <div className='address-row-sub'>
                      {addr.address?.street || addr.address || addr.fullAddress || ''}
                    </div>
                  </div>
                </div>
                <div className='address-row-right'>
                  <button
                    type='button'
                    className='address-edit-btn'
                    onClick={(e) => handleEditAddress(addr, e)}
                  >
                    Edit
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {addModal.isOpen && (
        <AddAddressModal
          isOpen={addModal.isOpen}
          toggle={addModal.closeModal}
          onAddressAdded={fetchAddresses}
          phoneNumber={userInfo?.phone || ''}
          hasNoAddresses={addresses.length === 0}
        />
      )}

      {editModal.isOpen && selectedAddress && (
        <EditAddressModal
          isOpen={editModal.isOpen}
          toggle={editModal.closeModal}
          address={selectedAddress}
          onAddressUpdated={fetchAddresses}
        />
      )}
    </div>
  )
}

export default AddressesTab
